export function getErrorMessage(error) {
    if (error instanceof Error) {
        return error.message;
    }
    if (typeof error === "string") {
        return error;
    }
    if (error && typeof error === "object") {
        try {
            return JSON.stringify(error);
        }
        catch {
            return "Unknown error";
        }
    }
    return "Unknown error";
}
export class HttpError extends Error {
    status;
    statusText;
    bodyText;
    apiErrorCode;
    apiErrorMessage;
    apiErrorDetail;
    constructor(params) {
        super(`Error ${params.status}: ${params.statusText} - ${params.bodyText}`);
        this.name = "HttpError";
        this.status = params.status;
        this.statusText = params.statusText;
        this.bodyText = params.bodyText;
        this.apiErrorCode = params.apiErrorCode;
        this.apiErrorMessage = params.apiErrorMessage;
        this.apiErrorDetail = params.apiErrorDetail;
    }
}
export function isHttpError(error) {
    return error instanceof HttpError;
}
/**
 * Rejects an `error.message` that is a serialized data structure rather than a
 * sentence (V2M-DEV-091).
 *
 * Both frontends' `getUserFriendlyMessage` end with the same fallback: for an
 * `apiErrorCode` they do not recognise, render `apiErrorMessage` verbatim in a
 * toast. That is only safe while the backend guarantees the field is prose.
 * It did not: `src/main.py`'s `HTTPException` handler used to compute
 * `message = str(detail)`, so the repo's dominant idiom
 * (`detail={"error": ..., "code": ...}`) produced a literal Python dict repr —
 * `{'error': 'We do not yet support...', 'code': 'REGISTRATION_MARKET_NOT_SUPPORTED'}` —
 * and the toast showed exactly that.
 *
 * The backend is fixed at the source. This is the second, independent line:
 * the clients must not blindly trust a field they render to users, so any
 * message that opens like an object/array literal is dropped here, at the
 * single point where the envelope is parsed. Dropping it (rather than patching
 * each `errorHandling.ts`) means both apps fall through to their own generic
 * message with no change to either file, and any future client gets the same
 * protection for free. `apiErrorDetail` still carries the full original
 * structure for code that knows the shape (e.g. `extractMarketGateError`).
 *
 * Conservative on purpose: only a leading `{` / `[` disqualifies a message. A
 * legitimate sentence never starts with one, and anything looser risks hiding
 * real backend prose from users.
 */
function looksLikeSerializedStructure(message) {
    const trimmed = message.trim();
    return trimmed.startsWith("{") || trimmed.startsWith("[");
}
function tryParseApiErrorEnvelope(bodyText) {
    try {
        const parsed = JSON.parse(bodyText);
        if (!parsed || typeof parsed !== "object") {
            return undefined;
        }
        const parsedRecord = parsed;
        const errorValue = parsedRecord["error"];
        const detailValue = parsedRecord["detail"];
        if (!errorValue || typeof errorValue !== "object") {
            return undefined;
        }
        const errorRecord = errorValue;
        const code = errorRecord["code"];
        const message = errorRecord["message"];
        const trustedMessage = typeof message === "string" && !looksLikeSerializedStructure(message) ? message : undefined;
        return {
            apiErrorCode: typeof code === "string" ? code : undefined,
            apiErrorMessage: trustedMessage,
            apiErrorDetail: detailValue,
        };
    }
    catch {
        return undefined;
    }
}
export function normalizeError(error) {
    if (isHttpError(error)) {
        const base = {
            kind: "http",
            message: error.message,
            status: error.status,
            bodyText: error.bodyText,
        };
        if (error.apiErrorCode !== undefined ||
            error.apiErrorMessage !== undefined ||
            error.apiErrorDetail !== undefined) {
            return {
                ...base,
                ...(error.apiErrorCode !== undefined ? { apiErrorCode: error.apiErrorCode } : {}),
                ...(error.apiErrorMessage !== undefined ? { apiErrorMessage: error.apiErrorMessage } : {}),
                ...(error.apiErrorDetail !== undefined ? { apiErrorDetail: error.apiErrorDetail } : {}),
            };
        }
        return {
            ...base,
        };
    }
    if (error instanceof TypeError) {
        return {
            kind: "network",
            message: error.message,
        };
    }
    return {
        kind: "unknown",
        message: getErrorMessage(error),
    };
}
export async function createHttpError(response) {
    const errorText = await response.text();
    const parsed = tryParseApiErrorEnvelope(errorText);
    return new HttpError({
        status: response.status,
        statusText: response.statusText,
        bodyText: errorText,
        ...(parsed ?? {}),
    });
}
/**
 * The four market-gate codes the registration/checkout country whitelist
 * raises (V2M-DEV-002, backend `services/payments/market_gate.py` +
 * `db/models/user.py`). Both flows fail closed the same way: a resolved-but-
 * unsupported country is `*_MARKET_NOT_SUPPORTED` (carries `country`); a
 * country that could not be resolved at all is `*_COUNTRY_UNRESOLVED` (no
 * `country` field — there is nothing to report).
 */
export const MARKET_GATE_ERROR_CODES = [
    "REGISTRATION_MARKET_NOT_SUPPORTED",
    "REGISTRATION_COUNTRY_UNRESOLVED",
    "CHECKOUT_MARKET_NOT_SUPPORTED",
    "CHECKOUT_COUNTRY_UNRESOLVED",
];
/**
 * Detects a market-gate 403 from a `NormalizedError` and pulls out its code
 * (+ country, when present).
 *
 * FastAPI's global `HTTPException` handler (`backend/voice2mail_proxy/src/
 * main.py`) wraps every raised `HTTPException(detail={...})` into
 * `{"error": {"code": "HTTP_<status>", "message": <str(detail) repr>},
 * "detail": {...original detail dict...}}` — so the market-gate code this
 * function looks for is NEVER in `apiErrorCode` (always the generic
 * `"HTTP_403"`); it only ever surfaces nested at `apiErrorDetail.code`.
 * Verified by executing `build_error_payload` directly against the exact
 * dict `market_gate.py`/`user.py` raise (2026-08-03) — not guessed from
 * reading the handler's source. Returns `null` for anything else (a
 * different 403, a non-403, a malformed/missing detail) so callers can
 * safely fall through to their generic error handler — same contract as
 * `x2mail_app`'s `parseActiveSubscriptionError`.
 */
export function extractMarketGateError(error) {
    if (error.status !== 403) {
        return null;
    }
    const detail = error.apiErrorDetail;
    if (!detail || typeof detail !== "object" || Array.isArray(detail)) {
        return null;
    }
    const record = detail;
    const code = record["code"];
    if (typeof code !== "string" || !MARKET_GATE_ERROR_CODES.includes(code)) {
        return null;
    }
    const country = record["country"];
    return {
        code: code,
        ...(typeof country === "string" ? { country } : {}),
    };
}
